import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useIsNarrow } from '../hooks/useIsNarrow';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ConfirmDialog, Field, inputCls } from '../components/ui/ConfirmDialog';
import { Section, ErrorPanel, LoadingRow, EmptyNote, KV, TableWrap, Th, Td } from '../components/ui/bits';
import { StatTile } from '../components/ui/StatTile';
import { fmtMinor } from '../lib/money';
import { useRpc, useSel, queryClient } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { adminApi, downloadJson } from '../lib/adminApi';
import { agoLabel, fmtDate, fmtDateTime } from '../lib/format';
import { classMeta } from '../lib/classes';
import { selfLabel, channelLabel } from '../lib/labels';
import { VYO_LIMITS } from '../lib/business';

type Dialog =
  | 'grant_comp' | 'revoke_comp' | 'grant_credits' | 'reset_monthly'
  | 'pause_alerts' | 'unpause_alerts' | 'resend_welcome' | 'phone_override' | null;

const TABS = ['overview', 'practice', 'money', 'alerts', 'emails', 'coach', 'growth', 'compliance', 'events'] as const;
const TAB_LABEL: Record<typeof TABS[number], string> = {
  overview: 'Overview', practice: 'Practice', money: 'Money', alerts: 'Alerts', emails: 'Emails',
  coach: 'Vyo', growth: 'Source and referrals', compliance: 'Compliance', events: 'Events',
};

export const UserDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') ?? 'overview') as typeof TABS[number];
  const [dialog, setDialog] = useState<Dialog>(null);
  const [exporting, setExporting] = useState(false);

  // Action form state
  const [compPlan, setCompPlan] = useState('pass');
  const [compDays, setCompDays] = useState('30');
  const [credits, setCredits] = useState({ cv: 0, psy: 0, hv: 0, mocks: 0, cover_letter: 0, research: 0 });

  const { data: u, error, isLoading } = useRpc<Record<string, any>>('admin_user_360', { p_user: id });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin_user_360'] });
    queryClient.invalidateQueries({ queryKey: ['user-detail', id] });
  };

  if (isLoading) return <LoadingRow label="Assembling the 360" />;
  if (error) return <ErrorPanel error={error} label="admin_user_360" />;
  if (!u || !u.auth) return <ErrorPanel error="No such user" />;

  const auth = u.auth ?? {};
  const profile = u.profile ?? {};
  const sub = u.subscription ?? {};
  const counts = u.counts ?? {};
  const fu = u.free_usage ?? {};
  const um = u.usage_month ?? null;
  const prefs = u.email_preferences ?? null;
  const sc = u.signup_context ?? null;
  const del = u.deletion_request ?? null;
  const passes: any[] = u.passes ?? [];
  const meta = classMeta(sub.class);
  const name = profile.full_name || auth.email || id.slice(0, 8);
  const attr = u.attribution ?? {};
  const vyo = u.vyo ?? {};
  const payer = u.payer ?? {};

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await adminApi<{ export: unknown }>('export_user', { userId: id });
      downloadJson(res.export, `intervyo-user-${id.slice(0, 8)}.json`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <Link to="/users" className="tap inline-flex items-center gap-1 text-[13px] lg:text-[12px] text-stone-500 lg:text-stone-400 hover:text-stone-700 mb-1 lg:mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> All users
      </Link>

      {del && del.status === 'pending' && (
        <Link to={`/compliance/deletions/${del.id}`} className="block mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 hover:border-rose-400 transition-colors">
          <span className="text-[13px] font-bold text-rose-700">Data-deletion request pending since {fmtDate(del.requested_at)}, scope {del.scope}.</span>
          <span className="block sm:inline text-[12px] text-rose-600 sm:ml-2">Open the fulfilment workflow</span>
        </Link>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h1 className="text-[22px] sm:text-2xl font-bold text-stone-900 tracking-tight">{name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[12px] text-stone-500">
            <Badge tone={meta.tone}>{meta.label}</Badge>
            {payer.now ? <Badge tone="emerald">Paying now</Badge> : payer.ever ? <Badge tone="stone">Paid before</Badge> : null}
            {u.internal && <Badge tone="amber">Internal account, excluded from metrics</Badge>}
            {passes.length > 0 && <Badge tone="violet">{passes.length} pack{passes.length > 1 ? 's' : ''}</Badge>}
            <span className="[overflow-wrap:anywhere] min-w-0">{auth.email}</span>
            <span>· joined {fmtDate(auth.created_at)}</span>
            <span>· last active {u.last_active ? agoLabel(u.last_active) : 'never'}</span>
            <span title="auth.users.last_sign_in_at">· last sign-in {agoLabel(auth.last_sign_in_at)}</span>
            <span>· {u.market === 'us' ? 'US' : 'UK'}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" onClick={() => setDialog('grant_comp')}>Grant comp</Button>
          <Button size="sm" onClick={() => setDialog('grant_credits')}>Grant credits</Button>
          <Button size="sm" onClick={() => setDialog('reset_monthly')}>Reset month</Button>
          <Button size="sm" onClick={() => setDialog('pause_alerts')}>Pause alerts</Button>
          <Button size="sm" onClick={() => setDialog('unpause_alerts')}>Unpause</Button>
          <Button size="sm" onClick={() => setDialog('resend_welcome')}>Resend welcome</Button>
          <Button size="sm" busy={exporting} onClick={exportData}>Export data</Button>
          {sub.class === 'comped' || sub.class === 'comped_expired' ? (
            <Button size="sm" variant="danger" onClick={() => setDialog('revoke_comp')}>Revoke comp</Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-5 xl:grid-cols-9 gap-2 mb-5 lg:mb-6">
        {[
          ['Sessions', counts.sessions, counts.sessions_failed ? `${counts.sessions_failed} failed` : ''],
          ['Psy runs', counts.psy_runs, ''],
          ['Vyo messages', counts.coach_msgs, ''],
          ['Tools', counts.tools, ''],
          ['Pipelines', counts.pipelines, ''],
          ['Bells', counts.bells_active, counts.bells_dead ? `${counts.bells_dead} dead` : ''],
          ['Emails sent', counts.emails_sent, ''],
          ['Referrals', `${counts.referrals_out ?? 0}/${counts.referrals_in ?? 0}`, 'out/in'],
          ['Events', counts.events, ''],
        ].map(([label, v, sub2]) => (
          <div key={String(label)} className="bg-white rounded-lg border border-stone-200 px-3 py-2.5">
            <p className="text-[12px] text-stone-500">{label}</p>
            <p className="text-[17px] font-semibold text-stone-900 tabular-nums">{v ?? 0}</p>
            {sub2 ? <p className="text-[11.5px] text-stone-500">{sub2}</p> : null}
          </div>
        ))}
      </div>

      <div className="tab-row no-scrollbar flex gap-1 mb-4 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => { const n = new URLSearchParams(params); n.set('tab', t); setParams(n, { replace: true }); }}
            className={[
              'shrink-0 px-3 py-2 text-[12.5px] font-semibold whitespace-nowrap border-b-2 transition-colors',
              tab === t ? 'border-indigo-600 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-800',
            ].join(' ')}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab profile={profile} sub={sub} fu={fu} um={um} prefs={prefs} sc={sc} attr={attr} />}
      {tab === 'practice' && <PracticeTab id={id} />}
      {tab === 'money' && <MoneyTab id={id} sub={sub} passes={passes} webhooks={u.webhooks ?? []} webhookLog={!!u.webhook_log_exists} onChanged={invalidate} />}
      {tab === 'alerts' && <AlertsTab id={id} />}
      {tab === 'emails' && <EmailsTab id={id} />}
      {tab === 'coach' && <CoachTab id={id} vyo={vyo} />}
      {tab === 'growth' && <GrowthTab id={id} sc={sc} attr={attr} onChanged={invalidate} />}
      {tab === 'compliance' && <ComplianceTab id={id} profile={profile} del={del} onOverride={() => setDialog('phone_override')} />}
      {tab === 'events' && <EventsTab id={id} />}

      {/* ---- dialogs ---- */}
      <ConfirmDialog
        open={dialog === 'grant_comp'} onClose={() => setDialog(null)}
        title="Grant comped access"
        body={<>Sets the subscription to the chosen plan with no Stripe link. Base and pass comps auto-expire on the end date via the hourly cron; pro never auto-expires.</>}
        confirmLabel="Grant"
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_grant_comp', { p_user: id, p_plan: compPlan, p_days: Number(compDays) });
          if (e) throw new Error(e.message);
          invalidate();
        }}
      >
        <Field label="Plan">
          <select value={compPlan} onChange={e => setCompPlan(e.target.value)} className={inputCls}>
            <option value="pass">Season Pass</option>
            <option value="base">Monthly</option>
            <option value="pro">Pro (never auto-expires)</option>
          </select>
        </Field>
        <Field label="Days">
          <input type="number" min={1} max={730} value={compDays} onChange={e => setCompDays(e.target.value)} className={inputCls} />
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'revoke_comp'} onClose={() => setDialog(null)}
        title="Revoke comped access" tone="danger" confirmLabel="Revoke"
        body={<>Reverts the subscription to free immediately, the same shape the expiry cron writes. Refuses Stripe-linked rows.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_revoke_comp', { p_user: id });
          if (e) throw new Error(e.message);
          invalidate();
        }}
      />

      <ConfirmDialog
        open={dialog === 'grant_credits'} onClose={() => setDialog(null)}
        title="Grant bonus credits"
        body={<>Adds to the free-tier bonus columns, additive on top of any welcome gift. The database reads these directly.</>}
        confirmLabel="Grant credits"
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_grant_bonus_credits', {
            p_user: id, p_cv: credits.cv, p_psy: credits.psy, p_hv: credits.hv,
            p_mocks: credits.mocks, p_cover_letter: credits.cover_letter, p_research: credits.research,
          });
          if (e) throw new Error(e.message);
          invalidate();
        }}
      >
        <div className="grid grid-cols-3 gap-2">
          {(Object.keys(credits) as (keyof typeof credits)[]).map(k => (
            <Field key={k} label={k.replace('_', ' ')}>
              <input
                type="number" min={0} max={50} value={credits[k]}
                onChange={e => setCredits({ ...credits, [k]: Number(e.target.value) })}
                className={inputCls}
              />
            </Field>
          ))}
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'reset_monthly'} onClose={() => setDialog(null)}
        title="Reset this month’s usage" confirmLabel="Reset"
        body={<>Zeroes every metered counter in the current calendar month for this user. Lifetime free counters are untouched.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_reset_monthly_usage', { p_user: id });
          if (e) throw new Error(e.message);
          invalidate();
        }}
      />

      <ConfirmDialog
        open={dialog === 'pause_alerts'} onClose={() => setDialog(null)}
        title="Pause all alerts" confirmLabel="Pause"
        body={<>Pauses every bell and broad alert this user holds. Nothing is deleted; unpause restores them.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_pause_user_alerts', { p_user: id });
          if (e) throw new Error(e.message);
          invalidate();
        }}
      />
      <ConfirmDialog
        open={dialog === 'unpause_alerts'} onClose={() => setDialog(null)}
        title="Unpause all alerts" confirmLabel="Unpause"
        body={<>Reactivates every paused bell and broad alert for this user.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_unpause_user_alerts', { p_user: id });
          if (e) throw new Error(e.message);
          invalidate();
        }}
      />

      <ConfirmDialog
        open={dialog === 'resend_welcome'} onClose={() => setDialog(null)}
        title="Resend the welcome email" confirmLabel="Send"
        body={<>Clears the sent stamp and fires the welcome email again through the normal pipeline. It still honours opt-outs.</>}
        onConfirm={async () => {
          await adminApi('resend_welcome', { userId: id });
          invalidate();
        }}
      />

      <ConfirmDialog
        open={dialog === 'phone_override'} onClose={() => setDialog(null)}
        title={profile.phone_verification_admin_override ? 'Remove phone verification override' : 'Override phone verification'}
        confirmLabel="Apply"
        body={<>Toggles the admin override that lets this account skip phone verification.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_set_phone_override', { p_user: id, p_value: !profile.phone_verification_admin_override });
          if (e) throw new Error(e.message);
          invalidate();
        }}
      />
    </div>
  );
};

/* ---------- tabs ---------- */

const AttributionPanel: React.FC<{ attr: any }> = ({ attr }) => {
  const ft = attr.first_touch ?? null;
  return (
    <Section title="Where they came from" sub="What they told us at onboarding, then what the browser recorded at first touch." source="profiles.onboarding_referral_source, profiles.signup_attribution">
      <dl>
        <KV k="Heard about us (self-reported)">{attr.self_reported ? selfLabel(attr.self_bucket) : 'Not answered'}{attr.self_reported_other ? `: ${attr.self_reported_other}` : ''}</KV>
        <KV k="First-touch channel">{channelLabel(attr.channel)}</KV>
        <KV k="Source and medium">{ft ? `${ft.utm_source ?? 'none'} / ${ft.utm_medium ?? 'none'}` : 'Not captured'}</KV>
        <KV k="Campaign">{ft?.utm_campaign ?? 'none'}</KV>
        <KV k="Landing page">{ft?.landing_page ?? 'none'}</KV>
        <KV k="Referrer">{ft?.referrer ?? 'none'}</KV>
        <KV k="Captured">{ft?.captured_at ? fmtDateTime(ft.captured_at) : 'none'}</KV>
        {ft?.relabelled_from && <KV k="Before the 27 Oct relabel">{typeof ft.relabelled_from === 'string' ? ft.relabelled_from : JSON.stringify(ft.relabelled_from)}</KV>}
      </dl>
    </Section>
  );
};

const OverviewTab: React.FC<{ profile: any; sub: any; fu: any; um: any; prefs: any; sc: any; attr: any }> = ({ profile, sub, fu, um, prefs, sc, attr }) => (
  <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
    <AttributionPanel attr={attr} />
    <Section title="Profile">
      <dl>
        <KV k="Full name">{profile.full_name ?? 'none'}</KV>
        <KV k="University">{profile.university ?? 'none'}</KV>
        <KV k="Degree">{profile.degree_subject ?? 'none'}{profile.graduation_year ? ` · ${profile.graduation_year}` : ''}</KV>
        <KV k="Stage">{profile.onboarding_stage ?? 'none'}</KV>
        <KV k="Industry">{profile.onboarding_industry ?? 'none'}</KV>
        <KV k="Onboarded">{profile.onboarding_completed ? fmtDate(profile.onboarding_completed_at) : 'no'}</KV>
        <KV k="CV uploaded">{profile.cv_uploaded ? 'yes' : 'no'}</KV>
        <KV k="Referral code">{profile.referral_code ?? 'none'}</KV>
        <KV k="Bonus firm slots">{profile.bonus_firm_slots ?? 0}</KV>
      </dl>
    </Section>
    <Section title="Access">
      <dl>
        <KV k="Class"><Badge tone={classMeta(sub.class).tone}>{classMeta(sub.class).label}</Badge></KV>
        <KV k="Plan">{sub.plan ?? 'free'}</KV>
        <KV k="Status">{sub.status ?? 'none'}</KV>
        <KV k="Billing interval">{sub.billing_interval ?? 'none'}</KV>
        <KV k="Period end">{fmtDate(sub.current_period_end)}</KV>
        <KV k="Cancels at period end">{sub.cancel_at_period_end ? 'yes' : 'no'}</KV>
        <KV k="Stripe subscription">{sub.stripe_subscription_id ?? 'none'}</KV>
        <KV k="Stripe customer">
          {sub.stripe_customer_id ? (
            <a className="text-indigo-600 hover:underline inline" target="_blank" rel="noreferrer"
               href={`https://dashboard.stripe.com/customers/${sub.stripe_customer_id}`}>
              {sub.stripe_customer_id} <ExternalLink className="w-3 h-3 inline align-[-1px]" />
            </a>
          ) : 'none'}
        </KV>
        <KV k="Email preferences">
          {prefs
            ? [prefs.receives_firm_alerts && 'alerts', prefs.receives_weekly_digest && 'digest', prefs.receives_product_updates && 'product'].filter(Boolean).join(', ') || 'all off'
            : 'defaults (all on)'}
          {prefs?.unsubscribed_at ? ' · globally unsubscribed' : ''}
        </KV>
      </dl>
    </Section>
    <Section title="Usage and gifts" sub="Free lifetime counters plus this month’s metered usage.">
      <dl>
        <KV k="CV checks">{fu.cv_checks_used ?? 0} used · +{fu.cv_bonus ?? 0} bonus</KV>
        <KV k="Psychometric">{fu.psychometric_used ?? 0} used · +{fu.psy_bonus ?? 0} bonus</KV>
        <KV k="HireVue questions">{fu.hirevue_questions_used ?? 0} used · +{fu.hv_bonus ?? 0} bonus</KV>
        <KV k="Live mocks">{fu.live_interviews_used ?? 0} used · +{fu.mocks_bonus ?? 0} bonus</KV>
        <KV k="Cover letters">{fu.cover_letters_used ?? 0} used · +{fu.cover_letter_bonus ?? 0} bonus</KV>
        <KV k="Research">{fu.research_used ?? 0} used · +{fu.research_bonus ?? 0} bonus</KV>
        <KV k="Welcome gift">{fu.first_week_gift_granted_at ? fmtDate(fu.first_week_gift_granted_at) : 'not granted'}</KV>
        <KV k="Referral reward">{fu.referral_reward_granted_at ? fmtDate(fu.referral_reward_granted_at) : 'not granted'}</KV>
        {um && <KV k="This month">{`hv ${um.hirevue_questions_used ?? 0} · psy ${um.psy_tests_used ?? 0} · cv ${um.cv_used ?? 0} · mocks ${um.live_sessions_used ?? 0}`}</KV>}
        {sc && <KV k="Entry source">{sc.entry_source ?? 'none'}</KV>}
      </dl>
    </Section>
  </div>
);

const PracticeTab: React.FC<{ id: string }> = ({ id }) => {
  const { data: sessions, error: e1 } = useSel<any[]>(['user-detail', id, 'sessions'], () =>
    supabase.from('sessions').select('id, type, status, title, overall_score, duration_seconds, created_at, share_token').eq('user_id', id).order('created_at', { ascending: false }).limit(30));
  const { data: runs, error: e2 } = useSel<any[]>(['user-detail', id, 'runs'], () =>
    supabase.from('psychometric_test_runs').select('id, firm_name, status, score, duration_seconds, created_at, test_id').eq('user_id', id).order('created_at', { ascending: false }).limit(20));
  const { data: pipelines, error: e3 } = useSel<any[]>(['user-detail', id, 'pipes'], () =>
    supabase.from('firm_pipelines').select('id, firm_name, division, career_stage, current_stage_index, stages, started_at, rejected_at, rejection_stage, completed_at, is_shadow').eq('user_id', id).order('started_at', { ascending: false }).limit(15));

  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Section title="Sessions" sub="Newest 30. Click through for the report, transcript and recordings.">
        {e1 && <ErrorPanel error={e1} />}
        {(sessions ?? []).length === 0 ? <EmptyNote>No sessions</EmptyNote> : (
          <TableWrap>
            <thead><tr><Th>Session</Th><Th>Status</Th><Th>Score</Th><Th>When</Th></tr></thead>
            <tbody>
              {(sessions ?? []).map(s => (
                <tr key={s.id}>
                  <Td>
                    <Link to={`/sessions/${s.id}`} className="text-indigo-600 hover:underline font-medium">{s.title || s.type}</Link>
                    <span className="text-stone-400 ml-1">{s.type}</span>
                    {s.share_token && <Badge tone="indigo" className="ml-1">Shared</Badge>}
                  </Td>
                  <Td><Badge tone={s.status === 'completed' ? 'emerald' : s.status === 'failed' ? 'rose' : 'amber'}>{s.status}</Badge></Td>
                  <Td className="tabular-nums">{s.overall_score ?? ''}</Td>
                  <Td className="whitespace-nowrap">{agoLabel(s.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Section>
      <div className="space-y-3">
        <Section title="Psychometric runs">
          {e2 && <ErrorPanel error={e2} />}
          {(runs ?? []).length === 0 ? <EmptyNote>No runs</EmptyNote> : (
            <TableWrap>
              <thead><tr><Th>Firm</Th><Th>Status</Th><Th>Score</Th><Th>When</Th></tr></thead>
              <tbody>
                {(runs ?? []).map(r => (
                  <tr key={r.id}>
                    <Td>{r.firm_name ?? 'general'}</Td>
                    <Td><Badge tone={r.status === 'completed' ? 'emerald' : r.status === 'abandoned' ? 'stone' : 'amber'}>{r.status}</Badge></Td>
                    <Td className="tabular-nums">{r.score ?? ''}</Td>
                    <Td className="whitespace-nowrap">{agoLabel(r.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>
        <Section title="Pipelines">
          {e3 && <ErrorPanel error={e3} />}
          {(pipelines ?? []).filter(p => !p.is_shadow).length === 0 ? <EmptyNote>No pipelines</EmptyNote> : (
            <div className="space-y-1.5">
              {(pipelines ?? []).filter(p => !p.is_shadow).map(p => (
                <div key={p.id} className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-3 gap-y-0.5 text-[12px]">
                  <span className="text-stone-800 font-medium truncate min-w-0 max-w-full">{p.firm_name}<span className="text-stone-400 font-normal"> · {p.career_stage ?? ''}</span></span>
                  <span>
                    {p.rejected_at ? <Badge tone="rose">Rejected{p.rejection_stage ? ` at ${p.rejection_stage}` : ''}</Badge>
                      : p.completed_at ? <Badge tone="emerald">Completed</Badge>
                      : <Badge tone="indigo">Stage {(p.current_stage_index ?? 0) + 1}{Array.isArray(p.stages) ? ` of ${p.stages.length}` : ''}</Badge>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

const MoneyTab: React.FC<{ id: string; sub: any; passes: any[]; webhooks: any[]; webhookLog: boolean; onChanged: () => void }> = ({ id, sub, passes, webhooks, webhookLog, onChanged }) => {
  const [stripeDetail, setStripeDetail] = useState<any>(null);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [extendPass, setExtendPass] = useState<any>(null);
  const [newExpiry, setNewExpiry] = useState('');
  const { data: stripeEvents } = useSel<any[]>(['user-detail', id, 'stripe-events'], () =>
    supabase.from('processed_stripe_events').select('event_type, session_id, processed_at').eq('user_id', id).order('processed_at', { ascending: false }).limit(20));

  const loadStripe = async () => {
    setLoadingStripe(true);
    try { setStripeDetail(await adminApi('stripe_customer', { userId: id })); }
    catch (e) { setStripeDetail({ error: (e as Error).message }); }
    finally { setLoadingStripe(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Section title="Firm passes" sub="Per-pass usage is lifetime for that pass.">
        {passes.length === 0 ? <EmptyNote>No passes</EmptyNote> : (
          <div className="space-y-3">
            {passes.map(fp => (
              <div key={fp.id} className="p-2.5 rounded-md border border-stone-100">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 mb-1">
                  <span className="text-[13px] font-semibold text-stone-900 min-w-0">{fp.firm_name ?? 'unknown firm'} · {fp.tier}</span>
                  <span className="flex items-center gap-1.5">
                    <Badge tone={fp.stripe_payment_intent ? 'emerald' : fp.stripe_session_id ? 'amber' : 'violet'} title={fp.stripe_payment_intent ? 'Stripe payment intent recorded' : fp.stripe_session_id ? 'Checkout session but no payment intent' : 'No Stripe link'}>
                      {fp.stripe_payment_intent ? 'Paid' : fp.stripe_session_id ? 'Session, no payment' : 'Comped'}
                    </Badge>
                    {fp.refunded_at && <Badge tone="rose">Refunded</Badge>}
                    <Button size="sm" onClick={() => { setExtendPass(fp); setNewExpiry((fp.expires_at ?? '').slice(0, 10)); }}>Extend</Button>
                  </span>
                </div>
                <p className="text-[11px] text-stone-400">
                  granted {fmtDate(fp.granted_at)} · expires {fmtDate(fp.expires_at)} · market {fp.market ?? 'uk'}
                </p>
                <p className="text-[11px] text-stone-500 mt-1">
                  hv {fp.usage_hv_used ?? 0} · psy {fp.usage_psy_used ?? 0} · mocks {fp.usage_mocks_used ?? 0} · cv {fp.usage_cv_used ?? 0} · research {fp.usage_research_used ?? 0} · vyo {fp.usage_vyo_used ?? 0}
                </p>
              </div>
            ))}
          </div>
        )}
      </Section>
      <div className="space-y-3">
        <Section
          title="Stripe"
          actions={<Button size="sm" busy={loadingStripe} onClick={loadStripe}>Load Stripe detail</Button>}
        >
          <dl>
            <KV k="Customer">{sub.stripe_customer_id ?? 'none'}</KV>
            <KV k="Subscription">{sub.stripe_subscription_id ?? 'none'}</KV>
          </dl>
          {stripeDetail?.error && <ErrorPanel error={stripeDetail.error} />}
          {stripeDetail?.invoices && (
            <TableWrap>
              <thead><tr><Th>Invoice</Th><Th>Status</Th><Th>Total</Th><Th>When</Th></tr></thead>
              <tbody>
                {stripeDetail.invoices.map((i: any) => (
                  <tr key={i.id}>
                    <Td><a className="text-indigo-600 hover:underline" href={i.hosted_invoice_url ?? '#'} target="_blank" rel="noreferrer">{i.id.slice(0, 18)}</a></Td>
                    <Td>{i.status}</Td>
                    <Td className="tabular-nums">{fmtMinor(i.total, String(i.currency))}</Td>
                    <Td>{fmtDate(i.created)}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>
        {webhookLog && (
          <Section title="Stripe webhook log" sub="Every webhook received for this user, newest 30." source="stripe_webhook_log">
            {webhooks.length === 0 ? <EmptyNote>None received</EmptyNote> : (
              <div className="space-y-1">
                {webhooks.map((w, i) => (
                  <div key={i} className="flex flex-wrap justify-between gap-x-2 gap-y-0.5 text-[12.5px]">
                    <span className="text-stone-700 min-w-0">{w.event_type}{w.amount_minor != null && w.currency ? ` · ${fmtMinor(w.amount_minor, w.currency)}` : ''}</span>
                    <span className="flex items-center gap-2">
                      <Badge tone={w.status === 'ok' ? 'emerald' : w.status === 'error' ? 'rose' : 'stone'}>{w.status}</Badge>
                      <span className="text-stone-400">{fmtDateTime(w.last_received_at)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}
        <Section title="Processed Stripe events" sub="The idempotency ledger for subscription and pack payments. In-app pack purchases may not appear here.">
          {(stripeEvents ?? []).length === 0 ? <EmptyNote>None recorded</EmptyNote> : (
            <div className="space-y-1">
              {(stripeEvents ?? []).map((e, i) => (
                <div key={i} className="flex flex-wrap justify-between gap-x-3 text-[12px]">
                  <span className="text-stone-700 min-w-0">{e.event_type}</span>
                  <span className="text-stone-400">{fmtDateTime(e.processed_at)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <ConfirmDialog
        open={!!extendPass} onClose={() => setExtendPass(null)}
        title={`Extend ${extendPass?.firm_name ?? ''} ${extendPass?.tier ?? ''} pass`}
        confirmLabel="Set expiry"
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_extend_pass', { p_pass_id: extendPass.id, p_expires: new Date(newExpiry + 'T23:59:59Z').toISOString() });
          if (e) throw new Error(e.message);
          onChanged();
        }}
      >
        <Field label="New expiry date">
          <input type="date" value={newExpiry} onChange={e => setNewExpiry(e.target.value)} className={inputCls} />
        </Field>
      </ConfirmDialog>
    </div>
  );
};

const AlertsTab: React.FC<{ id: string }> = ({ id }) => {
  const { data: bells, error: e1 } = useSel<any[]>(['user-detail', id, 'bells'], () =>
    supabase.from('user_firm_tracking').select('firm_name, sub_sector, programme_type, alerts_enabled, paused_at, created_at, locale').eq('user_id', id).order('created_at', { ascending: false }).limit(60));
  const { data: broad } = useSel<any[]>(['user-detail', id, 'broad'], () =>
    supabase.from('user_broad_alerts').select('sector, sub_sector, programme_type, enabled, paused_at, name').eq('user_id', id));
  const { data: sends } = useSel<any[]>(['user-detail', id, 'sends'], () =>
    supabase.from('alert_sends').select('channel, sent_at, firm_open_event_id').eq('user_id', id).order('sent_at', { ascending: false }).limit(20));

  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Section title="Bells" sub="A bell with no sub-sector can never match a typed open event (the known matcher gap).">
        {e1 && <ErrorPanel error={e1} />}
        {(bells ?? []).length === 0 ? <EmptyNote>No bells</EmptyNote> : (
          <TableWrap>
            <thead><tr><Th>Firm</Th><Th>Sub-sector</Th><Th>Programme</Th><Th>State</Th></tr></thead>
            <tbody>
              {(bells ?? []).map((b, i) => (
                <tr key={i}>
                  <Td className="font-medium text-stone-900">{b.firm_name}</Td>
                  <Td>{b.sub_sector ?? <Badge tone="rose">none, dead bell</Badge>}</Td>
                  <Td>{b.programme_type}</Td>
                  <Td>
                    {!b.alerts_enabled ? <Badge tone="stone">Off</Badge>
                      : b.paused_at ? <Badge tone="amber">Paused</Badge>
                      : <Badge tone="emerald">Active</Badge>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Section>
      <div className="space-y-3">
        <Section title="Broad alerts">
          {(broad ?? []).length === 0 ? <EmptyNote>None</EmptyNote> : (
            <div className="space-y-1">
              {(broad ?? []).map((b, i) => (
                <div key={i} className="flex flex-wrap justify-between gap-x-3 text-[12px]">
                  <span className="text-stone-700 min-w-0">{b.name ?? `${b.sector} · ${b.programme_type}`}</span>
                  <span>{b.enabled && !b.paused_at ? <Badge tone="emerald">Active</Badge> : <Badge tone="stone">Off</Badge>}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
        <Section title="Alert deliveries">
          {(sends ?? []).length === 0 ? <EmptyNote>Never alerted</EmptyNote> : (
            <div className="space-y-1">
              {(sends ?? []).map((s, i) => (
                <div key={i} className="flex justify-between text-[12px]">
                  <span className="text-stone-700">{s.channel}</span>
                  <span className="text-stone-400">{fmtDateTime(s.sent_at)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

const EmailsTab: React.FC<{ id: string }> = ({ id }) => {
  const { data, error } = useRpc<any[]>('admin_user_emails', { p_user: id });
  return (
    <Section title="Every email this user has been sent" sub="Lifecycle, alerts, welcome and pack claims, unified.">
      {error && <ErrorPanel error={error} />}
      {(data ?? []).length === 0 ? <EmptyNote>No emails recorded</EmptyNote> : (
        <TableWrap>
          <thead><tr><Th>System</Th><Th>Email</Th><Th>Sent</Th><Th>Reference</Th></tr></thead>
          <tbody>
            {(data ?? []).map((e, i) => (
              <tr key={i}>
                <Td>{e.source}</Td>
                <Td className="font-medium text-stone-900">{e.label}</Td>
                <Td className="whitespace-nowrap">{fmtDateTime(e.sent_at)}</Td>
                <Td className="text-stone-400">{e.ref ? String(e.ref).slice(0, 18) : ''}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Section>
  );
};

const CoachTab: React.FC<{ id: string; vyo: any }> = ({ id, vyo }) => {
  const [open, setOpen] = useState<string | null>(null);
  const narrow = useIsNarrow(767);
  const messagesRef = useRef<HTMLDivElement>(null);
  // On a phone the messages sit under the list, so picking a conversation has to bring them into view.
  useEffect(() => {
    if (open && narrow) messagesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const { data: convos, error } = useSel<any[]>(['user-detail', id, 'convos'], () =>
    supabase.from('coach_conversations').select('id, title, firm_id, created_at, updated_at').eq('user_id', id).order('updated_at', { ascending: false }).limit(30));
  const { data: msgs } = useSel<any[]>(['user-detail', id, 'msgs', open], () =>
    supabase.from('coach_messages').select('role, content, action, created_at').eq('conversation_id', open!).order('created_at').limit(80),
    { enabled: !!open });

  return (
    <div className="space-y-3">
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <StatTile label="Messages sent, lifetime" value={vyo.messages_lifetime ?? 0} source="coach_messages, role user" />
      <StatTile label="Messages, 30 days" value={vyo.messages_30d ?? 0} />
      <StatTile label="In pack chats" value={vyo.messages_pack ?? 0} sub="Firm pack allowance, not the plan limit" />
      <StatTile label={vyo.tier === 'paid' ? 'Plan limit used this month' : 'Free limit used, lifetime'}
        value={`${vyo.ledger_used ?? 0} of ${vyo.limit ?? (vyo.tier === 'paid' ? VYO_LIMITS.subscription_monthly : VYO_LIMITS.free_lifetime)}`}
        tone={(vyo.ledger_used ?? 0) >= (vyo.limit ?? 8) ? 'warn' : 'default'}
        source="coach_usage_log, what coach-chat enforces" />
      <StatTile label="Vyo tier" value={vyo.tier === 'paid' ? 'Paid' : 'Free'} sub={vyo.tier === 'paid' ? 'Resets on the 1st (UTC)' : 'Lifetime allowance'} />
    </div>
    <div className="grid md:grid-cols-2 gap-3">
      <Section title="Vyo conversations" sub="Pack-scoped chats carry a firm id.">
        {error && <ErrorPanel error={error} />}
        {(convos ?? []).length === 0 ? <EmptyNote>Never talked to Vyo</EmptyNote> : (
          <div className="space-y-1">
            {(convos ?? []).map(c => (
              <button key={c.id} onClick={() => setOpen(c.id)}
                className={`w-full flex justify-between items-center gap-3 text-left px-2 py-1.5 rounded-md text-[12.5px] ${open === c.id ? 'bg-indigo-50' : 'hover:bg-stone-50'}`}>
                <span className="truncate min-w-0 text-stone-800">{c.title || 'untitled'}{c.firm_id && <Badge tone="violet" className="ml-1.5">Pack</Badge>}</span>
                <span className="text-stone-400 whitespace-nowrap">{agoLabel(c.updated_at)}</span>
              </button>
            ))}
          </div>
        )}
      </Section>
      <div ref={messagesRef} className="scroll-mt-3">
      <Section title="Messages">
        {!open ? <EmptyNote>Select a conversation</EmptyNote> : (
          <div className="space-y-2 max-h-[75dvh] md:max-h-[480px] overflow-y-auto">
            {(msgs ?? []).map((m, i) => (
              <div key={i} className={`p-2 rounded-md text-[12px] leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-stone-50 text-stone-800' : 'bg-indigo-50 text-stone-900'}`}>
                <span className="block text-[10px] font-semibold mb-0.5 opacity-60">{m.role === 'user' ? 'User' : 'Vyo'} · {fmtDateTime(m.created_at)}</span>
                {String(m.content)}
                {m.action && (
                  <span className="mt-1.5 flex items-center gap-1 text-[10.5px] font-semibold text-indigo-700">
                    <span className="rounded-md border border-indigo-200 bg-white px-1.5 py-0.5">
                      Card: {m.action.label ?? 'action'} opens {m.action.tab ?? m.action.section ?? '?'}{m.action.locked ? ', locked' : ''}
                    </span>
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>
      </div>
    </div>
    </div>
  );
};

const GrowthTab: React.FC<{ id: string; sc: any; attr: any; onChanged: () => void }> = ({ id, sc, attr, onChanged }) => {
  const [release, setRelease] = useState<any>(null);
  const { data: refsOut } = useSel<any[]>(['user-detail', id, 'refs-out'], () =>
    supabase.from('referrals').select('id, invitee_id, claimed_at, rewarded_at, flagged_at, fraud_score, code_used').eq('inviter_id', id));
  const { data: refsIn } = useSel<any[]>(['user-detail', id, 'refs-in'], () =>
    supabase.from('referrals').select('id, inviter_id, claimed_at, rewarded_at, code_used').eq('invitee_id', id));
  const { data: demos } = useSel<any[]>(['user-detail', id, 'demos'], () =>
    supabase.from('demo_sessions').select('feature, status, created_at, claimed_at').eq('user_id', id).limit(20));

  return (
    <div className="grid md:grid-cols-2 gap-3">
      <AttributionPanel attr={attr} />
      <Section title="Signup intent" sub="The last intent the marketing link carried (firm, pack). Overwritten on later visits, so not a first-touch source." source="signup_context">
        {!sc ? <EmptyNote>No signup context captured</EmptyNote> : (
          <dl>
            <KV k="Entry source">{sc.entry_source ?? 'none'}</KV>
            <KV k="Firm intent">{sc.firm_slug ?? 'none'}</KV>
            <KV k="Pack intent">{sc.desired_pack ?? 'none'}</KV>
            <KV k="Query string">{sc.raw_query_string || 'none'}</KV>
            <KV k="Referrer">{sc.referrer_url || 'none'}</KV>
          </dl>
        )}
      </Section>
      <Section title="Referrals" sub="Out: people they invited. In: who invited them.">
        {(refsOut ?? []).length + (refsIn ?? []).length === 0 ? <EmptyNote>No referral activity</EmptyNote> : (
          <div className="space-y-1.5 text-[12px]">
            {(refsOut ?? []).map(r => (
              <div key={r.id} className="flex items-center justify-between gap-2">
                <span>Invited <Link className="text-indigo-600" to={`/users/${r.invitee_id}`}>{String(r.invitee_id).slice(0, 8)}</Link></span>
                <span className="flex items-center gap-1.5">
                  {r.rewarded_at ? <Badge tone="emerald">Rewarded</Badge>
                    : r.flagged_at ? (
                      <>
                        <Badge tone="rose">Held, score {r.fraud_score ?? '?'}</Badge>
                        <Button size="sm" onClick={() => setRelease(r)}>Release</Button>
                      </>
                    ) : <Badge tone="amber">Pending</Badge>}
                </span>
              </div>
            ))}
            {(refsIn ?? []).map(r => (
              <div key={r.id} className="flex items-center justify-between gap-2">
                <span>Invited by <Link className="text-indigo-600" to={`/users/${r.inviter_id}`}>{String(r.inviter_id).slice(0, 8)}</Link></span>
                <span>{r.rewarded_at ? <Badge tone="emerald">Rewarded</Badge> : <Badge tone="amber">Pending</Badge>}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
      <Section title="Demos claimed">
        {(demos ?? []).length === 0 ? <EmptyNote>None</EmptyNote> : (
          <div className="space-y-1 text-[12px]">
            {(demos ?? []).map((d, i) => (
              <div key={i} className="flex justify-between">
                <span className="text-stone-700">{d.feature}</span>
                <span className="text-stone-400">{fmtDate(d.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <ConfirmDialog
        open={!!release} onClose={() => setRelease(null)}
        title="Release the held referral payout" confirmLabel="Release"
        body={<>Clears the fraud hold so the sweep cron pays this referral on its next pass. The fraud score stays recorded.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_release_referral_hold', { p_referral_id: release.id });
          if (e) throw new Error(e.message);
          onChanged();
        }}
      />
    </div>
  );
};

const ComplianceTab: React.FC<{ id: string; profile: any; del: any; onOverride: () => void }> = ({ id, profile, del, onOverride }) => {
  const [revoke, setRevoke] = useState<any>(null);
  const { data: shares } = useSel<any[]>(['user-detail', id, 'shares'], () =>
    supabase.from('sessions').select('id, type, title, shared_at, share_token').eq('user_id', id).not('share_token', 'is', null));

  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Section title="Consent and verification">
        <dl>
          <KV k="Terms accepted">{profile.tos_accepted_at ? `${fmtDate(profile.tos_accepted_at)} (${profile.tos_version ?? 'unknown version'})` : 'no'}</KV>
          <KV k="Phone verified">{profile.phone_verified_at ? fmtDate(profile.phone_verified_at) : 'no'}</KV>
          <KV k="Verification required">{profile.phone_verification_required ? 'yes' : 'no'}</KV>
          <KV k="Admin override">{profile.phone_verification_admin_override ? 'yes' : 'no'}</KV>
        </dl>
        <div className="mt-2">
          <Button size="sm" onClick={onOverride}>
            {profile.phone_verification_admin_override ? 'Remove verification override' : 'Override phone verification'}
          </Button>
        </div>
      </Section>
      <Section title="Shared reports" sub="Share links are permanent by design; revoking removes the token.">
        {(shares ?? []).length === 0 ? <EmptyNote>Nothing shared</EmptyNote> : (
          <div className="space-y-1.5">
            {(shares ?? []).map(s => (
              <div key={s.id} className="flex items-center justify-between gap-3 text-[12px]">
                <span className="text-stone-700 truncate min-w-0">{s.title || s.type} · shared {fmtDate(s.shared_at)}</span>
                <Button size="sm" variant="danger" onClick={() => setRevoke(s)}>Revoke</Button>
              </div>
            ))}
          </div>
        )}
      </Section>
      <Section title="Deletion">
        {del ? (
          <div className="text-[12.5px] text-stone-700">
            Request {del.status}, made {fmtDate(del.requested_at)}.
            {del.status === 'pending' && (
              <Link to={`/compliance/deletions/${del.id}`} className="text-indigo-600 font-semibold ml-1">Open the workflow</Link>
            )}
          </div>
        ) : <EmptyNote>No deletion request on file</EmptyNote>}
      </Section>

      <ConfirmDialog
        open={!!revoke} onClose={() => setRevoke(null)}
        title="Revoke this shared report" tone="danger" confirmLabel="Revoke"
        body={<>The public link stops working immediately. The report itself is untouched.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_revoke_share', { p_session_id: revoke.id });
          if (e) throw new Error(e.message);
          queryClient.invalidateQueries({ queryKey: ['user-detail', id, 'shares'] });
        }}
      />
    </div>
  );
};

const EventsTab: React.FC<{ id: string }> = ({ id }) => {
  const { data, error } = useSel<any[]>(['user-detail', id, 'events'], () =>
    supabase.from('events').select('event_type, event_data, created_at').eq('user_id', id).order('created_at', { ascending: false }).limit(60));
  return (
    <Section title="Event timeline" sub="Newest 60. The full explorer is under General, Events.">
      {error && <ErrorPanel error={error} />}
      {(data ?? []).length === 0 ? <EmptyNote>No events</EmptyNote> : (
        <div className="space-y-1">
          {(data ?? []).map((e, i) => (
            <details key={i} className="text-[12px]">
              <summary className="cursor-pointer flex flex-wrap items-center justify-between gap-x-3 py-1 hover:bg-stone-50 rounded px-1">
                <span className="font-medium text-stone-800 min-w-0">{e.event_type}</span>
                <span className="text-stone-400">{fmtDateTime(e.created_at)}</span>
              </summary>
              <pre className="text-[10.5px] bg-stone-50 rounded-md p-2 overflow-x-auto text-stone-600">{JSON.stringify(e.event_data, null, 2)}</pre>
            </details>
          ))}
        </div>
      )}
    </Section>
  );
};
